import type { QueryClient } from "@tanstack/react-query";
import { commercialViewOf, tabKey, type TabContext, type TabKey } from "../session/tab-context";

// Le chiavi del menu delle Smart List e la sua rilettura dopo una scrittura sui Lead (revisione del 3 ottobre 2026). Stanno
// qui, senza altre dipendenze, perché le usano sia le Smart List sia le scritture dei Lead, che le Smart List importano.
//
// Con la rail aperta e fissa (da 1280 px) il sottomenu non si riapre mai, quindi numeri e «novità» non si rileggerebbero
// più da soli: dopo «Nuovo Lead», una modifica della scheda, «Chi vede i Lead», una riassegnazione o i «Campi della scheda»
// il menu si rilegge. Costa poco: soltanto il menu della vista della scheda e soltanto se è già stato letto (nessuna voce
// nuova nella cache, nessuna lettura per chi non vede le liste), anche con la rail a icone e chiusa, così il pallino
// sull'icona segue; una lettura in corso non si raddoppia mai (P26: un menu conta fino a cento liste, due alla volta), e
// finita lei se ne fa una sola in più, per quante scritture siano arrivate nel frattempo, perché poteva essere di prima.
// I menu delle altre viste diventano soltanto vecchi e si rileggono quando tornano in vista. Nel contesto di piattaforma
// si rilegge soltanto un menu che qualcuno sta guardando: ogni lettura resta nel registro dell'azienda (D13).

/** Il prefisso del menu di una scheda, in ogni vista. */
export const smartListMenuPrefix = (tab: TabKey) => ["scheda", tab, "smart-lists"] as const;

/** Il menu di una scheda in una vista (`null`: la propria), con i conteggi del perimetro di quella vista. */
export const smartListMenuKey = (tab: TabKey, view: string | null) => ["scheda", tab, "smart-lists", view] as const;

/** Le letture in corso dopo le quali è già prevista una rilettura, per `QueryClient`. */
const queued = new WeakMap<QueryClient, Set<string>>();

export function refreshSmartListMenu(queryClient: QueryClient, tab: TabContext): void {
  const part = tabKey(tab);
  if (tab.kind !== "membership") {
    void queryClient.invalidateQueries({ queryKey: smartListMenuPrefix(part) });
    return;
  }
  void queryClient.invalidateQueries({ queryKey: smartListMenuPrefix(part), refetchType: "none" });
  const query = queryClient.getQueryCache().find({ queryKey: smartListMenuKey(part, commercialViewOf(tab)), exact: true });
  if (!query) return;
  const reread = () => void query.fetch().catch(() => undefined);
  if (query.state.fetchStatus !== "fetching") {
    reread();
    return;
  }
  const waiting = queued.get(queryClient) ?? new Set<string>();
  queued.set(queryClient, waiting);
  if (waiting.has(query.queryHash)) return;
  waiting.add(query.queryHash);
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.query !== query) return;
    if (event.type !== "removed" && query.state.fetchStatus === "fetching") return;
    unsubscribe();
    waiting.delete(query.queryHash);
    if (event.type !== "removed") reread();
  });
}
